import { useMutation } from '@tanstack/react-query';
import { useAppContext } from '../../contexts/Storage';
import { apiClient } from '../../utils/api';
import { UrlBuilder } from '../../utils/urls';
import {
  createCheckoutSession,
  type CreateCheckoutSessionParams,
} from '../../utils/stripe';
import type { SubscriptionPlan } from './use-subscriptions';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CheckoutParams {
  plan: SubscriptionPlan;
  billingPeriod: 'monthly' | 'yearly';
}

export interface ConfirmSubscriptionParams {
  plan_id: number;
  payment_method: string;
  payment_details?: {
    session_id?: string;
    [key: string]: unknown;
  };
}

export interface ConfirmSubscriptionResponse {
  message?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// useCreateCheckoutSession
// ---------------------------------------------------------------------------

export function useCreateCheckoutSession() {
  const { user } = useAppContext();

  return useMutation<{ id: string; url: string }, Error, CheckoutParams>({
    mutationFn: async ({ plan, billingPeriod }) => {
      const email = user?.email;
      if (!email) throw new Error('User email is required for checkout');

      const params: CreateCheckoutSessionParams = {
        planName: plan.subscription_name
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase()),
        unitAmountCents: Math.round(plan.subscription_price * 100),
        billingInterval: billingPeriod === 'yearly' ? 'year' : 'month',
        customerEmail: email,
        metadata: {
          plan_id: String(plan.id),
          user_id: String(user?.id ?? ''),
          plan_name: plan.subscription_name,
        },
      };

      const session = await createCheckoutSession(params);
      return session;
    },
  });
}

// ---------------------------------------------------------------------------
// useConfirmSubscription
// ---------------------------------------------------------------------------

export function useConfirmSubscription() {
  const { token } = useAppContext();

  return useMutation<ConfirmSubscriptionResponse, Error, ConfirmSubscriptionParams>({
    mutationFn: async (params) => {
      if (!token) throw new Error('User not authenticated');

      const response = await apiClient.postWithToken<ConfirmSubscriptionResponse>(
        UrlBuilder.subscriptions.subscribe(),
        token,
        params
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to confirm subscription');
      }

      return response.data as ConfirmSubscriptionResponse;
    },
  });
}
